import { useEffect, useEffectEvent, useRef } from 'react';

import { useLocation } from 'react-router';

import { useSetAtom } from 'jotai';

import { resetAllScrollAtom } from '@/hooks/useScrollMemory/state';

import { resetAllDataViewAtom } from '@/atoms/data-view';

/**
 * Extracts the first segment of a pathname (e.g., '/reports/plans' -> '/reports')
 */
const getFirstPathSegment = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length > 0 ? `/${segments[0]}` : pathname;
};

/**
 * Resets all scroll positions and data-view state when navigating away from the section.
 * Both resets happen together when leaving the section is detected.
 *
 * @param sectionPath - The path prefix for this section (e.g., '/plans', '/reports')
 */
export const useResetViewStateOnSectionLeave = (sectionPath: string) => {
  const resetScroll = useSetAtom(resetAllScrollAtom);
  const resetDataView = useSetAtom(resetAllDataViewAtom);
  const location = useLocation();

  // Use refs to persist values across renders without causing re-renders
  const prevFirstSegmentRef = useRef<string | null>(null);
  const prevSectionPathRef = useRef<string | null>(null);

  // Use useEffectEvent to avoid re-creating the effect when resetScroll, resetDataView, or sectionPath changes
  const handleLocationChange = useEffectEvent(() => {
    const currentPathname = location.pathname;
    const currentFirstSegment = getFirstPathSegment(currentPathname);

    // Only compare if we have a previous pathname (skip first render)
    // Compare first segments to avoid resetting when navigating within the same section
    if (
      prevFirstSegmentRef.current !== null &&
      prevFirstSegmentRef.current !== currentFirstSegment
    ) {
      // If we're leaving the section (previous sectionPath matches prevFirstSegment, current doesn't), reset
      // Use prevSectionPath (the previous section's path) instead of current sectionPath
      if (
        prevSectionPathRef.current !== null &&
        prevFirstSegmentRef.current === prevSectionPathRef.current &&
        currentFirstSegment !== prevFirstSegmentRef.current
      ) {
        resetScroll();
        resetDataView();
      }
    }

    // Update the refs after comparison
    prevFirstSegmentRef.current = currentFirstSegment;
    prevSectionPathRef.current = sectionPath;
  });

  // Track location changes and reset both scroll and data-view together when leaving the section
  useEffect(() => {
    handleLocationChange();
  }, [location.pathname]);
};
